/**
 * Theme resolution rules (canon `contracts/DESIGN-TOKENS.md` § Theme mechanics and
 * § Cross-surface persistence):
 *
 * - `system` is the default and follows `prefers-color-scheme`.
 * - `light` and `dark` are explicit user overrides and persist across **all three** Dealbid
 *   surfaces, in a cookie on the parent domain, mirrored into `localStorage`.
 * - an unreadable or unrecognised stored value falls back to `system` rather than
 *   guessing, so a corrupted key can never leave the page in the wrong theme.
 *
 * This module is pure so the same rules can be unit-tested and mirrored by the no-flash
 * bootstrap script in index.html. THEME_STORAGE_KEY, THEME_ATTRIBUTE and the cookie name
 * are asserted against index.html by test/theme/bootstrap.test.ts to keep the two in step.
 *
 * `dealbid-web` is the reference implementation (`CANON-009`, `WEB-038`); `dealbid-app` and
 * `dealbid-admin` copy these rules rather than inventing their own cookie.
 */

import type { AppEnvironment } from "../config/env";

export const THEME_STORAGE_KEY = "dealbid.theme";
export const THEME_ATTRIBUTE = "data-theme";

/**
 * The cookie's name prefix. The environment is appended, always — `staging.dealbid.app` is a
 * subdomain of `dealbid.app`, so a production cookie on `.dealbid.app` is visible on staging,
 * and two same-named cookies on one host make `document.cookie` ambiguous. Scoping by domain
 * alone cannot isolate these environments; the name does it, with no special case.
 */
export const THEME_COOKIE_PREFIX = "dealbid_theme_";

/** One year, refreshed on each explicit choice. */
export const THEME_COOKIE_MAX_AGE_SECONDS = 31_536_000;

export interface ThemeCookieSpec {
  name: string;
  /** Undefined for local, which is host-only on a loopback address. */
  domain: string | undefined;
  secure: boolean;
}

/**
 * Where the cookie is readable from, per environment.
 *
 * The leading dot is what makes it visible to every subdomain — the whole point of the
 * mechanism. Local omits a domain entirely: `127.0.0.1` has no parent to share with, and the
 * four loopback surfaces already share it because cookies ignore the port.
 */
const THEME_COOKIE_DOMAINS: Record<AppEnvironment, string | undefined> = {
  local: undefined,
  staging: ".staging.dealbid.app",
  production: ".dealbid.app",
};

export function themeCookieSpec(environment: AppEnvironment): ThemeCookieSpec {
  return {
    name: `${THEME_COOKIE_PREFIX}${environment}`,
    domain: THEME_COOKIE_DOMAINS[environment],
    // Plain http is a local-development convenience only, and `Secure` on a plain-http origin
    // means the cookie is silently never stored.
    secure: environment !== "local",
  };
}

/** The value of one cookie in a `document.cookie` string, or undefined if it is not there. */
function cookieValue(cookieString: string, name: string): string | undefined {
  for (const part of cookieString.split(";")) {
    const separator = part.indexOf("=");
    if (separator === -1) continue;
    if (part.slice(0, separator).trim() !== name) continue;
    return part.slice(separator + 1).trim();
  }
  return undefined;
}

/**
 * The preference the cookie carries, or undefined when it carries none.
 *
 * `system` is the cookie's *absence*, so a cookie holding the literal string `system` is as
 * unrecognised as one holding `neon`: both fall through to the caller's fallback.
 */
export function readThemeCookie(
  cookieString: string,
  name: string,
): "light" | "dark" | undefined {
  const value = cookieValue(cookieString, name);
  return value === "light" || value === "dark" ? value : undefined;
}

/**
 * The string to assign to `document.cookie` to record a preference.
 *
 * `system` produces a deletion — an expired, empty cookie — rather than the literal string,
 * so the default has exactly one representation.
 */
export function themeCookieAssignment(
  spec: ThemeCookieSpec,
  preference: ThemePreference,
): string {
  const parts = [
    preference === "system" ? `${spec.name}=` : `${spec.name}=${preference}`,
    "Path=/",
    "SameSite=Lax",
    preference === "system" ? "Max-Age=0" : `Max-Age=${THEME_COOKIE_MAX_AGE_SECONDS}`,
  ];
  if (spec.domain !== undefined) parts.push(`Domain=${spec.domain}`);
  // Never HttpOnly: the pre-paint script in index.html has to read this before any module
  // runs, and a script cannot read a cookie the server hid from it.
  if (spec.secure) parts.push("Secure");
  return parts.join("; ");
}

export const THEME_PREFERENCES = ["system", "light", "dark"] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

/** The theme actually painted, once `system` has been resolved. */
export type ResolvedTheme = "light" | "dark";

export const DEFAULT_THEME_PREFERENCE: ThemePreference = "system";

export const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

export function isThemePreference(value: unknown): value is ThemePreference {
  return (
    typeof value === "string" &&
    (THEME_PREFERENCES as readonly string[]).includes(value)
  );
}

export function normalizeThemePreference(value: unknown): ThemePreference {
  return isThemePreference(value) ? value : DEFAULT_THEME_PREFERENCE;
}

export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme {
  if (preference === "system") {
    return systemPrefersDark ? "dark" : "light";
  }
  return preference;
}

/**
 * Applies the preference to the document. `system` removes the attribute entirely so the
 * stylesheet's `prefers-color-scheme` media query stays in control — the browser then
 * keeps up with an OS theme change without any JavaScript running.
 */
export function applyThemePreference(
  root: HTMLElement,
  preference: ThemePreference,
): void {
  if (preference === "system") {
    root.removeAttribute(THEME_ATTRIBUTE);
    return;
  }
  root.setAttribute(THEME_ATTRIBUTE, preference);
}

export function readStoredThemePreference(
  storage: Pick<Storage, "getItem"> | undefined,
): ThemePreference {
  if (!storage) return DEFAULT_THEME_PREFERENCE;
  try {
    return normalizeThemePreference(storage.getItem(THEME_STORAGE_KEY));
  } catch {
    // Private-mode or blocked storage must not break rendering.
    return DEFAULT_THEME_PREFERENCE;
  }
}

/**
 * The visitor's preference, from wherever it is recorded.
 *
 * The cookie wins where both exist: it is the cross-surface record and the stored value is
 * its mirror. The fallback is what lets the three repositories adopt this in any order
 * without a window in which somebody's choice is lost, and it also covers a browser
 * configured to block cookies but allow storage (canon `contracts/DESIGN-TOKENS.md`
 * § Cross-surface persistence).
 */
export function readPersistedThemePreference(
  cookieString: string,
  storage: Pick<Storage, "getItem"> | undefined,
  spec: ThemeCookieSpec,
): ThemePreference {
  return readThemeCookie(cookieString, spec.name) ?? readStoredThemePreference(storage);
}

/** Somewhere `document.cookie` can be read from and written to. */
export interface CookieJar {
  cookie: string;
}

/**
 * Records the preference in both places.
 *
 * Neither write may throw: persistence is a convenience and failing to store must never
 * block the theme change, which has already been applied to the document by then.
 */
export function writePersistedThemePreference(
  jar: CookieJar | undefined,
  storage: Pick<Storage, "setItem" | "removeItem"> | undefined,
  spec: ThemeCookieSpec,
  preference: ThemePreference,
): void {
  if (jar) {
    try {
      jar.cookie = themeCookieAssignment(spec, preference);
    } catch {
      // Blocked cookies leave the mirror below as the record.
    }
  }
  writeStoredThemePreference(storage, preference);
}

export function writeStoredThemePreference(
  storage: Pick<Storage, "setItem" | "removeItem"> | undefined,
  preference: ThemePreference,
): void {
  if (!storage) return;
  try {
    if (preference === "system") {
      storage.removeItem(THEME_STORAGE_KEY);
      return;
    }
    storage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Persistence is a convenience; failing to store must never block the theme change.
  }
}
